@file:Suppress(
    "RETURN_VALUE_NOT_USED",
    "RETURN_VALUE_NOT_USED_COERCION",
)

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Suppress
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import pl.michal_cyran.brand_hub.components.layouts.MarkdownLayout as _pl_michal_cyran_brand_hub_components_layouts_MarkdownLayout
import pl.michal_cyran.brand_hub.components.layouts.PageLayout as _pl_michal_cyran_brand_hub_components_layouts_PageLayout
import pl.michal_cyran.brand_hub.pages.AboutPage as _pl_michal_cyran_brand_hub_pages_AboutPage
import pl.michal_cyran.brand_hub.pages.DayMathPage as _pl_michal_cyran_brand_hub_pages_DayMathPage
import pl.michal_cyran.brand_hub.pages.FreelancingPage as _pl_michal_cyran_brand_hub_pages_FreelancingPage
import pl.michal_cyran.brand_hub.pages.IndexPage as _pl_michal_cyran_brand_hub_pages_IndexPage
import pl.michal_cyran.brand_hub.pages.TutoringPage as _pl_michal_cyran_brand_hub_pages_TutoringPage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "BrandHub"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "pl.michal_cyran.brand_hub.components.layouts.MarkdownLayout",
            parentLayoutId = "pl.michal_cyran.brand_hub.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                pl.michal_cyran.brand_hub.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _pl_michal_cyran_brand_hub_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "pl.michal_cyran.brand_hub.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _pl_michal_cyran_brand_hub_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "pl.michal_cyran.brand_hub.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                pl.michal_cyran.brand_hub.pages.initIndexPage(ctx)
            },
        ) { pageCtx -> 
            _pl_michal_cyran_brand_hub_pages_IndexPage()
        }
        ctx.router.register(
            "/about",
            layoutId = "pl.michal_cyran.brand_hub.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                pl.michal_cyran.brand_hub.pages.initAboutPage(ctx)
            },
        ) { pageCtx -> 
            _pl_michal_cyran_brand_hub_pages_AboutPage()
        }
        ctx.router.register(
            "/day-math",
            layoutId = "pl.michal_cyran.brand_hub.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                pl.michal_cyran.brand_hub.pages.initDayMathPage(ctx)
            },
        ) { pageCtx -> 
            _pl_michal_cyran_brand_hub_pages_DayMathPage()
        }
        ctx.router.register(
            "/freelancing",
            layoutId = "pl.michal_cyran.brand_hub.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                pl.michal_cyran.brand_hub.pages.initFreelancingPage(ctx)
            },
        ) { pageCtx -> 
            _pl_michal_cyran_brand_hub_pages_FreelancingPage()
        }
        ctx.router.register(
            "/tutoring",
            layoutId = "pl.michal_cyran.brand_hub.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                pl.michal_cyran.brand_hub.pages.initTutoringPage(ctx)
            },
        ) { pageCtx -> 
            _pl_michal_cyran_brand_hub_pages_TutoringPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("headline-text", pl.michal_cyran.brand_hub.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", pl.michal_cyran.brand_hub.SubheadlineTextStyle)
        ctx.theme.registerStyle("base-card", pl.michal_cyran.brand_hub.BaseCardStyle)
        ctx.theme.registerStyle("card", pl.michal_cyran.brand_hub.CardStyle)
        ctx.theme.registerStyle("featured-card", pl.michal_cyran.brand_hub.FeaturedCardStyle)
        ctx.theme.registerStyle("big-badge", pl.michal_cyran.brand_hub.BigBadgeStyle)
        ctx.theme.registerStyle("markdown", pl.michal_cyran.brand_hub.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("page-content", pl.michal_cyran.brand_hub.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("nav-header", pl.michal_cyran.brand_hub.components.sections.NavHeaderStyle)
        ctx.theme.registerStyle("nameplate", pl.michal_cyran.brand_hub.components.widgets.NameplateStyle)
        ctx.theme.registerStyle("name-first", pl.michal_cyran.brand_hub.components.widgets.NameFirstStyle)
        ctx.theme.registerStyle("name-last", pl.michal_cyran.brand_hub.components.widgets.NameLastStyle)
        ctx.theme.registerStyle("divider", pl.michal_cyran.brand_hub.components.widgets.DividerStyle)
        ctx.theme.registerStyle("tagline", pl.michal_cyran.brand_hub.components.widgets.TaglineStyle)
        ctx.theme.registerStyle("btn", pl.michal_cyran.brand_hub.BtnStyle)
        ctx.theme.registerStyle("btn-primary", pl.michal_cyran.brand_hub.BtnPrimaryStyle)
        ctx.theme.registerStyle("btn-outline", pl.michal_cyran.brand_hub.BtnOutlineStyle)
        ctx.theme.registerStyle("btn-lg", pl.michal_cyran.brand_hub.BtnLgStyle)
        ctx.theme.registerStyle("dark-section", pl.michal_cyran.brand_hub.DarkSectionStyle)
        ctx.theme.registerStyle("hero-container", pl.michal_cyran.brand_hub.HeroContainerStyle)
        ctx.theme.registerStyle("hero-grid", pl.michal_cyran.brand_hub.HeroGridStyle)
        ctx.theme.registerStyle("hero-badge", pl.michal_cyran.brand_hub.HeroBadgeStyle)
        ctx.theme.registerStyle("hero-badge-dot", pl.michal_cyran.brand_hub.HeroBadgeDotStyle)
        ctx.theme.registerStyle("hero-badge-text", pl.michal_cyran.brand_hub.HeroBadgeTextStyle)
        ctx.theme.registerStyle("hero-title", pl.michal_cyran.brand_hub.HeroTitleStyle)
        ctx.theme.registerStyle("hero-description", pl.michal_cyran.brand_hub.HeroDescriptionStyle)
        ctx.theme.registerStyle("hero-buttons", pl.michal_cyran.brand_hub.HeroButtonsStyle)
        ctx.theme.registerStyle("hero-social", pl.michal_cyran.brand_hub.HeroSocialStyle)
        ctx.theme.registerStyle("social-link", pl.michal_cyran.brand_hub.SocialLinkStyle)
        ctx.theme.registerStyle("hero-visual", pl.michal_cyran.brand_hub.HeroVisualStyle)
        ctx.theme.registerStyle("hero-visual-inner", pl.michal_cyran.brand_hub.HeroVisualInnerStyle)
        ctx.theme.registerStyle("hero-visual-logo", pl.michal_cyran.brand_hub.HeroVisualLogoStyle)
        ctx.theme.registerStyle("hero-visual-tags", pl.michal_cyran.brand_hub.HeroVisualTagsStyle)
        ctx.theme.registerStyle("hero-visual-tag", pl.michal_cyran.brand_hub.HeroVisualTagStyle)
        ctx.theme.registerStyle("light-section", pl.michal_cyran.brand_hub.LightSectionStyle)
        ctx.theme.registerStyle("services-title", pl.michal_cyran.brand_hub.ServicesTitleStyle)
        ctx.theme.registerStyle("services-subtitle", pl.michal_cyran.brand_hub.ServicesSubtitleStyle)
        ctx.theme.registerStyle("services-grid", pl.michal_cyran.brand_hub.ServicesGridStyle)
        ctx.theme.registerStyle("service-card", pl.michal_cyran.brand_hub.ServiceCardStyle)
        ctx.theme.registerStyle("icon-box", pl.michal_cyran.brand_hub.IconBoxStyle)
        ctx.theme.registerStyle("featured-icon-box", pl.michal_cyran.brand_hub.FeaturedIconBoxStyle)
        ctx.theme.registerStyle("footer", pl.michal_cyran.brand_hub.FooterStyle)
        ctx.theme.registerStyle("footer-container", pl.michal_cyran.brand_hub.FooterContainerStyle)
        ctx.theme.registerStyle("footer-grid", pl.michal_cyran.brand_hub.FooterGridStyle)
        ctx.theme.registerStyle("footer-contact-email", pl.michal_cyran.brand_hub.FooterContactEmailStyle)
        ctx.theme.registerStyle("footer-contact-box", pl.michal_cyran.brand_hub.FooterContactBoxStyle)
        ctx.theme.registerStyle("footer-bottom", pl.michal_cyran.brand_hub.FooterBottomStyle)
        ctx.theme.registerStyle("features-grid", pl.michal_cyran.brand_hub.FeaturesGridStyle)
        ctx.theme.registerStyle("small-text", pl.michal_cyran.brand_hub.SmallText)
        ctx.theme.registerStyle("first-grid", pl.michal_cyran.brand_hub.subpages.tutoring.sections.FirstGridStyle)
        ctx.theme.registerStyle("second-grid", pl.michal_cyran.brand_hub.subpages.tutoring.sections.SecondGridStyle)
        ctx.theme.registerVariant("-circle", pl.michal_cyran.brand_hub.CircleButtonVariant
        )
        ctx.theme.registerVariant("-uncolored", pl.michal_cyran.brand_hub.UncoloredButtonVariant
        )
        ctx.theme.registerKeyframes("side-menu-slide-in", pl.michal_cyran.brand_hub.components.sections.SideMenuSlideInAnim)
        ctx.theme.registerKeyframes("fade-up", pl.michal_cyran.brand_hub.components.widgets.FadeUp)
        ctx.theme.registerKeyframes("grow-line", pl.michal_cyran.brand_hub.components.widgets.GrowLine)
        ctx.theme.registerKeyframes("pulse", pl.michal_cyran.brand_hub.Pulse)
        pl.michal_cyran.brand_hub.initColorMode(ctx)
        pl.michal_cyran.brand_hub.initStyles(ctx)
        pl.michal_cyran.brand_hub.initSiteStyles(ctx)
        pl.michal_cyran.brand_hub.initTheme(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        pl.michal_cyran.brand_hub.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
